import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Users, ArrowRight, Laptop, Cloud, Check } from 'lucide-react';

const CohortCalendarWaitlist = () => {
  return (
    <section 
      id="programs"
      className="relative py-24 px-4 overflow-hidden bg-[#F9FAFB]"
    >
      <div className="max-w-[1400px] mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-[#0F172A] mb-4" style={{ fontFamily: 'Plus Jakarta Sans, Inter, sans-serif' }}>
            Upcoming 12-Week Cohorts
          </h2>
          <p className="text-xl text-[#4B5563]">
            Secure your spot in the next intake
          </p>
        </div>

        {/* Intake Date Banner */}
        <div 
          className="relative rounded-[20px] p-6 mb-12 text-center border border-[#E5E7EB]"
          style={{ 
            background: 'linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)',
            boxShadow: '0 8px 25px rgba(37, 99, 235, 0.15)'
          }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 mb-3 rounded-full bg-white border border-[#2563EB]/20">
            <Calendar size={16} className="text-[#2563EB]" />
            <span className="text-xs font-bold uppercase tracking-wide text-[#2563EB]">Next Intake Starts</span>
          </div>
          <div className="text-3xl md:text-4xl font-bold text-[#0F172A]" style={{ fontFamily: 'Plus Jakarta Sans, Inter, sans-serif' }}>
            Saturday, 7 December 2024
          </div>
        </div>

        {/* Two Program Cards */}
        <div className="grid md:grid-cols-2 gap-8">
          
          {/* CARD 1: SYSTEM & NETWORK ENGINEER */}
          <div 
            className="relative rounded-[24px] p-8 bg-white border border-[#E5E7EB] transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
          >
            {/* Left Colored Strip */}
            <div className="absolute left-0 top-8 bottom-8 w-1 bg-gradient-to-b from-[#16A34A] to-[#06B6D4] rounded-r-full"></div>

            {/* Top Labels */}
            <div className="flex items-center gap-3 mb-6">
              <span className="px-4 py-1.5 rounded-full text-sm font-semibold bg-[#16A34A] text-white">
                Beginner Friendly
              </span>
              <span className="text-sm font-medium text-[#6B7280]">12 weeks • In-person classroom</span>
            </div>

            {/* Icon & Title */}
            <div className="flex items-center gap-4 mb-6">
              <div className="inline-flex p-3 rounded-full bg-gradient-to-br from-[#06B6D4]/20 to-[#16A34A]/20">
                <Laptop size={32} className="text-[#16A34A]" strokeWidth={1.5} />
              </div>
              <h3 className="text-2xl font-bold text-[#0F172A]" style={{ fontFamily: 'Plus Jakarta Sans, Inter, sans-serif' }}>
                System and Network Engineer
              </h3>
            </div>

            {/* Features List */}
            <ul className="space-y-3 mb-6">
              <li className="flex items-start gap-3">
                <Check size={20} className="flex-shrink-0 mt-0.5 text-[#16A34A]" strokeWidth={2.5} />
                <span className="text-sm text-[#4B5563]">3 in-person sessions per week with live demonstrations and Q&A</span>
              </li>
              <li className="flex items-start gap-3">
                <Check size={20} className="flex-shrink-0 mt-0.5 text-[#16A34A]" strokeWidth={2.5} />
                <span className="text-sm text-[#4B5563]">Weekly guided labs on real routers, switches, Wi-Fi APs, laptops, and printers</span>
              </li>
              <li className="flex items-start gap-3">
                <Check size={20} className="flex-shrink-0 mt-0.5 text-[#16A34A]" strokeWidth={2.5} />
                <span className="text-sm text-[#4B5563]">Practice 15+ real office tickets: Wi-Fi, VPN, DNS, login issues, printer issues</span>
              </li>
              <li className="flex items-start gap-3">
                <Check size={20} className="flex-shrink-0 mt-0.5 text-[#16A34A]" strokeWidth={2.5} />
                <span className="text-sm text-[#4B5563]">Fully mapped to CompTIA A+ and Network+ exam objectives</span>
              </li>
            </ul>

            {/* Job Outcomes */}
            <div className="mb-6 p-4 rounded-xl bg-gradient-to-br from-[#EFF6FF] to-[#DBEAFE]">
              <p className="text-xs font-semibold uppercase tracking-wide text-[#2563EB] mb-2">Job Outcomes</p>
              <p className="text-sm font-semibold text-[#0F172A]">IT Support, Desktop Support, Service Desk, Junior Network Technician</p>
            </div>

            {/* Pricing */}
            <div className="mb-6 p-4 rounded-xl bg-gradient-to-br from-[#F0FDF4] to-[#DCFCE7] border border-[#16A34A]/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-[#16A34A] mb-1">Special Offer</p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold text-[#0F172A]">$1,499</span>
                    <span className="text-sm text-[#6B7280] line-through">$2,499</span>
                  </div>
                </div>
                <div className="px-3 py-1 rounded-full bg-[#16A34A] text-white text-xs font-bold">
                  40% OFF
                </div>
              </div>
            </div>

            {/* Seat Availability */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Users size={16} className="text-[#6B7280]" />
                  <span className="text-sm font-semibold text-[#0F172A]">20 of 50 seats left</span>
                </div>
                <span className="text-sm font-bold text-[#16A34A]">40%</span>
              </div>
              <div className="w-full h-2 bg-[#F3F4F6] rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[#16A34A] to-[#06B6D4] rounded-full" style={{ width: '40%' }}></div>
              </div>
            </div>

            {/* CTA */}
            <Link
              to="/contact"
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full font-semibold text-base transition-all duration-300 bg-[#16A34A] hover:bg-[#15803d] text-white hover:shadow-lg"
            >
              Enroll Now
              <ArrowRight size={20} />
            </Link>
          </div>

          {/* CARD 2: DEVOPS & CLOUD ENGINEER PROGRAM */}
          <div 
            className="relative rounded-[24px] p-8 bg-white border border-[#E5E7EB] transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
          >
            {/* Left Colored Strip */}
            <div className="absolute left-0 top-8 bottom-8 w-1 bg-gradient-to-b from-[#2563EB] to-[#6366F1] rounded-r-full"></div>

            {/* Top Labels */}
            <div className="flex items-center gap-3 mb-6">
              <span className="px-4 py-1.5 rounded-full text-sm font-semibold bg-[#2563EB] text-white">
                Intermediate
              </span>
              <span className="text-sm font-medium text-[#6B7280]">12 weeks • 1.5–2 hours per day</span>
            </div>

            {/* Icon & Title */}
            <div className="flex items-center gap-4 mb-6">
              <div className="inline-flex p-3 rounded-full bg-gradient-to-br from-[#2563EB]/20 to-[#6366F1]/20">
                <Cloud size={32} className="text-[#2563EB]" strokeWidth={1.5} />
              </div>
              <h3 className="text-2xl font-bold text-[#0F172A]" style={{ fontFamily: 'Plus Jakarta Sans, Inter, sans-serif' }}>
                DevOps & Cloud Engineer Program
              </h3>
            </div>

            {/* Features List */}
            <ul className="space-y-3 mb-6">
              <li className="flex items-start gap-3">
                <Check size={20} className="flex-shrink-0 mt-0.5 text-[#2563EB]" strokeWidth={2.5} />
                <span className="text-sm text-[#4B5563]">Linux, Git, networking and cloud fundamentals (Weeks 1\u20132)</span>
              </li>
              <li className="flex items-start gap-3">
                <Check size={20} className="flex-shrink-0 mt-0.5 text-[#2563EB]" strokeWidth={2.5} />
                <span className="text-sm text-[#4B5563]">Terraform infrastructure as code across AWS, Azure or GCP</span>
              </li>
              <li className="flex items-start gap-3">
                <Check size={20} className="flex-shrink-0 mt-0.5 text-[#2563EB]" strokeWidth={2.5} />
                <span className="text-sm text-[#4B5563]">Docker, Kubernetes, CI/CD pipelines using GitHub Actions or Jenkins</span>
              </li>
              <li className="flex items-start gap-3">
                <Check size={20} className="flex-shrink-0 mt-0.5 text-[#2563EB]" strokeWidth={2.5} />
                <span className="text-sm text-[#4B5563]">Cloud security, monitoring, AIOps and a full capstone portfolio project</span>
              </li>
            </ul>

            {/* Job Outcomes */}
            <div className="mb-6 p-4 rounded-xl bg-gradient-to-br from-[#EFF6FF] to-[#E0E7FF]">
              <p className="text-xs font-semibold uppercase tracking-wide text-[#2563EB] mb-2">Job Outcomes</p>
              <p className="text-sm font-semibold text-[#0F172A]">Junior DevOps Engineer, Cloud Engineer, SRE, Platform Engineer</p>
            </div>

            {/* Pricing */}
            <div className="mb-6 p-4 rounded-xl bg-gradient-to-br from-[#EFF6FF] to-[#DBEAFE] border border-[#2563EB]/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-[#2563EB] mb-1">Special Offer</p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold text-[#0F172A]">$1,999</span>
                    <span className="text-sm text-[#6B7280] line-through">$3,332</span>
                  </div>
                </div>
                <div className="px-3 py-1 rounded-full bg-[#2563EB] text-white text-xs font-bold">
                  40% OFF
                </div>
              </div>
            </div>

            {/* Seat Availability */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Users size={16} className="text-[#6B7280]" />
                  <span className="text-sm font-semibold text-[#0F172A]">15 of 50 seats left</span>
                </div>
                <span className="text-sm font-bold text-[#F59E0B]">30%</span>
              </div>
              <div className="w-full h-2 bg-[#F3F4F6] rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[#2563EB] to-[#6366F1] rounded-full" style={{ width: '30%' }}></div>
              </div>
            </div>

            {/* CTA */}
            <Link
              to="/contact"
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full font-semibold text-base transition-all duration-300 bg-[#2563EB] hover:bg-[#1d4ed8] text-white hover:shadow-lg"
            >
              Enroll Now
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CohortCalendarWaitlist;
